<script setup lang="ts">
import { useBackendService } from "~/composables/services/useBackend.service";
import type { ProductType } from "@/types";
import { InformationCircleIcon } from "@heroicons/vue/24/solid";
import OnePassFeature from "./OnePassFeature.vue";

const props = defineProps({
  section: {
    type: Object,
    required: true,
  },
  content: {
    type: Object,
    required: true,
  },
  // Add support for additional props passed from DynamicZone
  id: {
    type: [String, Number],
    required: false,
    default: null,
  },
  customId: {
    type: String,
    required: false,
    default: null,
  },
  __typename: {
    type: String,
    required: false,
    default: null,
  },
});

// Define details from content to prevent runtime errors
const details = computed(() => {
  return props.content || {};
});

const gotProducts = ref<boolean>(false);
const tabActive = ref();
const currentSlide = ref(0);
const slideCount = ref(0);
const isMobile = ref(false);
const loading = ref(true);

const products = ref<ProductType[]>([]);
const { getProducts } = useBackendService();

const fetchData = async () => {
  try {
    // Show loading state immediately
    gotProducts.value = false;
    loading.value = true;

    const allProducts = await getProducts();
    console.log("Raw products data received:", allProducts);

    // Add defensive check for allProducts
    if (!allProducts || !Array.isArray(allProducts)) {
      console.warn("Products API returned invalid data:", allProducts);
      products.value = [];
      slideCount.value = 0;
      loading.value = false;
      gotProducts.value = false;
      return;
    }

    // Filter products based on props.content.products if they exist
    let filteredProducts;
    if (
      props.content.products &&
      Array.isArray(props.content.products) &&
      props.content.products.length > 0
    ) {
      filteredProducts = allProducts.filter(
        (product: ProductType) =>
          product && product.id && props.content.products.includes(product.id)
      );
    } else {
      filteredProducts = allProducts;
    }

    console.log(
      "SORTING DEBUG - Before sorting, first two products:",
      filteredProducts.slice(0, 2).map((p) => ({
        name: p.name,
        price: getProductPrice(p),
        data: getProductDataGB(p),
        featured: isProductFeatured(p),
        category: p.category,
      }))
    );

    // CRITICAL FIX: Apply price sorting immediately after fetching products
    // This ensures products are sorted by price regardless of API's pre-sorting
    products.value = [...filteredProducts].sort((a, b) => {
      const aPrice = getProductPrice(a) || 0;
      const bPrice = getProductPrice(b) || 0;

      // If prices are equal (within a small threshold for float comparison)
      if (Math.abs(aPrice - bPrice) < 0.01) {
        // Secondary sort by data amount (highest first)
        const aData = getProductDataGB(a) || 0;
        const bData = getProductDataGB(b) || 0;
        return bData - aData; // Reverse order for data (highest first)
      }

      // Primary sort by price (lowest first)
      return aPrice - bPrice;
    });

    console.log(
      "SORTING DEBUG - After sorting, first two products:",
      products.value.slice(0, 2).map((p) => ({
        name: p.name,
        price: getProductPrice(p),
        data: getProductDataGB(p),
        featured: isProductFeatured(p),
        category: p.category,
      }))
    );

    // Update slide count for navigation (with defensive check)
    slideCount.value = Array.isArray(products.value)
      ? products.value.length
      : 0;

    // We'll use a slight delay to ensure spinner is visible even for fast loads
    setTimeout(() => {
      gotProducts.value = true;
      loading.value = false;
    }, 500);
  } catch (error) {
    console.error("Error fetching products:", error);
    products.value = []; // Ensure products is always an array
    gotProducts.value = false;
    loading.value = false;
  }
};

// Slider navigation functions
const slideNext = () => {
  const slidesToShow = isMobile.value ? 1 : 4;
  if (currentSlide.value < activeProducts.value.length - slidesToShow) {
    currentSlide.value += slidesToShow;
  }
};

const slidePrev = () => {
  if (currentSlide.value > 0) {
    currentSlide.value = Math.max(
      0,
      currentSlide.value - (isMobile.value ? 1 : 4)
    );
  }
};

const activePeriod = ref("monthly");
const sectionTitle = computed(() => props.content.heading || "Mobile Plans");
const categories = computed(() => props.content.categories || []);

// Load saved category from localStorage
const getSavedCategory = () => {
  if (process.client) {
    return localStorage.getItem("onepass-active-category") || "";
  }
  return "";
};

// Save category selection to localStorage
const saveCategory = (categoryCode) => {
  if (process.client) {
    localStorage.setItem("onepass-active-category", categoryCode);
  }
};

// Predefined list of categories to exclude completely
const excludedCategories = [
  "AF_FINDER", // Exclude specifically by exact match
  "AF_", // Exclude any that start with AF_
];

// Helper to determine if a category should be excluded
const shouldExcludeCategory = (category) => {
  if (!category || !category.code) return false;
  // Uppercase for case-insensitive comparison
  const code = category.code.toUpperCase();

  // Check if the category code is in the excluded list or starts with any excluded prefix
  return excludedCategories.some((excluded) => {
    if (excluded.endsWith("_")) {
      // For patterns ending with _, check if the category starts with this pattern
      return code.startsWith(excluded);
    } else {
      // For exact matches
      return code === excluded;
    }
  });
};

// Initialize data on component mount
onMounted(() => {
  fetchData();
  checkMobile();
  window.addEventListener("resize", checkMobile);

  // Set default category after data is loaded
  watch(
    () => products.value,
    () => {
      if (products.value.length > 0 && !tabActive.value) {
        initializeCategory();
      }
    },
    { immediate: true }
  );
});

// Initialize with CatAll or first available category
const initializeCategory = () => {
  let savedCategory = getSavedCategory();

  // Skip saved category if it starts with AF_
  if (savedCategory && savedCategory.toUpperCase().startsWith("AF_")) {
    console.log("Skipping saved AF_ category:", savedCategory);
    savedCategory = ""; // Reset if it's an AF_ category
  }

  // If saved category exists and is in available categories, use it
  if (savedCategory && uniqueCategories.value) {
    const categoryExists = uniqueCategories.value.some(
      (category) => category.code === savedCategory
    );

    if (categoryExists) {
      tabActive.value = savedCategory;
      return;
    }
  }

  // Create a filtered list without AF_ categories
  const safeCategories = uniqueCategories.value.filter(
    (cat) => !cat.code || !cat.code.toUpperCase().startsWith("AF_")
  );

  // Otherwise use CatAll, all, or first available category
  if (safeCategories && safeCategories.length > 0) {
    const hasAll = safeCategories.some(
      (cat) => cat.code === "CatAll" || cat.code === "all"
    );

    if (hasAll) {
      tabActive.value = safeCategories.find(
        (cat) => cat.code === "CatAll" || cat.code === "all"
      ).code;
    } else {
      // Fallback to first category
      tabActive.value = safeCategories[0].code;
    }
  } else {
    // If no categories, set to empty to show all products
    tabActive.value = "";
  }
};

const changeTab = (tabCode) => {
  tabActive.value = tabCode;
  localStorage.setItem("selectedTab", tabCode);

  // Reset filters based on the new category selection
  calculateRanges();
};

// Get unique categories for tabs if needed
const uniqueCategories = computed(() => {
  const categoriesMap = new Map();

  // Add defensive null/undefined check for products
  if (!products.value || !Array.isArray(products.value)) {
    console.log(
      "Products is not an array or is null/undefined:",
      products.value
    );
    return [];
  }

  // Process all products
  products.value.forEach((product) => {
    // Add defensive check for product
    if (!product) return;

    if (
      product.category &&
      Array.isArray(product.category) &&
      product.category.length > 0
    ) {
      // Loop through all categories of each product
      product.category.forEach((category) => {
        // Skip null categories
        if (!category) return;

        // Only include categories with parentId (excluding root categories)
        if (category.parentId) {
          if (
            !categoriesMap.has(category.code) &&
            !shouldExcludeCategory(category)
          ) {
            categoriesMap.set(category.code, category.name || category.code);
          }
        }
      });
    }
  });

  // Check for our priority categories
  const hasCatAll = categoriesMap.has("CatAll");
  const hasOnePass = categoriesMap.has("OnePass");
  let result = [];

  // Always include 'All Plans' option first
  result.push({ code: "all", name: "All Plans" });

  // Add OnePass Mobile as a priority category if it exists
  if (hasOnePass) {
    result.push({ code: "OnePass", name: categoriesMap.get("OnePass") });
    categoriesMap.delete("OnePass");
  }

  // Add CatAll next if it exists
  if (hasCatAll) {
    result.push({ code: "CatAll", name: categoriesMap.get("CatAll") });
    categoriesMap.delete("CatAll");
  }

  // Check if Hot Deals category exists and add it next
  if (
    categoriesMap.has("CatHotDeals") ||
    categoriesMap.has("Special_Hot_Deals")
  ) {
    const hotDealsKey = categoriesMap.has("CatHotDeals")
      ? "CatHotDeals"
      : "Special_Hot_Deals";
    result.push({ code: hotDealsKey, name: categoriesMap.get(hotDealsKey) });
    categoriesMap.delete(hotDealsKey);
  }

  // 3. Add the rest alphabetically
  const sortedCategories = Array.from(categoriesMap)
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Add sorted categories after our priority ones
  result.push(...sortedCategories);

  return result;
});

// Get the price for a product (for sorting)
const getProductPrice = (product: ProductType) => {
  if (
    product?.productOfferingPrice?.recurring &&
    product.productOfferingPrice.recurring.length > 0
  ) {
    // Find the first recurring price with a taxIncludedAmountAfterDiscount
    const priceInfo = product.productOfferingPrice.recurring.find(
      (item) => item?.price?.taxIncludedAmountAfterDiscount !== undefined
    );
    return priceInfo?.price?.taxIncludedAmountAfterDiscount || Number.MAX_VALUE;
  }
  return Number.MAX_VALUE; // Very high price for products without a price (sort to end)
};

// MODIFIED: This function previously checked if a product is featured, causing products
// to be sorted by featured status first. Now we always return false to ensure products
// are strictly sorted by price.
const isProductFeatured = (product: ProductType) => {
  // Old logic commented out to document the change while preserving history:
  // if (
  //   product?.offerServiceTemplate &&
  //   product.offerServiceTemplate.length > 0
  // ) {
  //   // Look through all service templates
  //   return product.offerServiceTemplate.some((template) => {
  //     return (
  //       template?.serviceTemplate?.customFields?.pim?.goCommerce
  //         ?.isFeaturedProduct === true
  //     );
  //   });
  // }
  // Always return false to prevent "featured" products from being prioritized
  return false;
};

// Filtered active products based on selected tab with sorting
// Filter toggle
const showFilters = ref(false);

// Range filter values
const priceRange = ref({
  min: 0,
  max: 200,
  currentMin: 0, // Current minimum selection
  currentMax: 200, // Current maximum selection
});

const dataRange = ref({
  min: 0,
  max: 100,
  currentMin: 0, // Current minimum selection
  currentMax: 100, // Current maximum selection
});

// Get data amount (GB) for a product
const getProductDataGB = (product: ProductType) => {
  if (
    product?.offerServiceTemplate &&
    product.offerServiceTemplate.length > 0
  ) {
    // Look through service templates for data quota
    for (const template of product.offerServiceTemplate) {
      const quotaDataGb =
        template?.serviceTemplate?.customFields?.pim?.goCommerce?.quotaDataGb;
      if (quotaDataGb !== undefined && quotaDataGb !== null) {
        return quotaDataGb;
      }
    }
  }
  return 0; // Default if no data found
};

// Calculate price and data ranges from products
// Helper function for calculating tooltip position percentage
const getPositionPercent = (current, min, max) => {
  // Calculate position as percentage of the range
  return ((current - min) / (max - min)) * 100;
};

// Functions for updating tooltips during slider movement
const updatePriceTooltips = () => {
  // This function gets called when price sliders are dragged
  // The tooltips are positioned using inline styles bound to the model values
};

const updateDataTooltips = () => {
  // This function gets called when data sliders are dragged
  // The tooltips are positioned using inline styles bound to the model values
};

const calculateRanges = () => {
  if (products.value.length === 0) return;

  // Filter products based on current category selection
  const filteredProducts =
    tabActive.value === "all" || !tabActive.value
      ? products.value
      : products.value.filter(
          (product) =>
            product.category &&
            product.category.some((cat) => cat.code === tabActive.value)
        );

  // Find min/max price
  let minPrice = Number.MAX_VALUE;
  let maxPrice = 0;

  // Find min/max data
  let minData = Number.MAX_VALUE;
  let maxData = 0;

  filteredProducts.forEach((product) => {
    // Process price
    const price = getProductPrice(product);
    if (price < Number.MAX_VALUE) {
      minPrice = Math.min(minPrice, price);
      maxPrice = Math.max(maxPrice, price);
    }

    // Process data
    const dataGB = getProductDataGB(product);
    if (dataGB > 0) {
      minData = Math.min(minData, dataGB);
      maxData = Math.max(maxData, dataGB);
    }
  });

  // Update ranges, avoiding invalid values
  if (minPrice < Number.MAX_VALUE) {
    const minVal = Math.floor(minPrice);
    const maxVal = Math.ceil(maxPrice);
    priceRange.value = {
      min: minVal,
      max: maxVal,
      currentMin: minVal, // Start with full range
      currentMax: maxVal,
    };
  }

  if (minData < Number.MAX_VALUE) {
    const minVal = Math.floor(minData);
    const maxVal = Math.ceil(maxData);
    dataRange.value = {
      min: minVal,
      max: maxVal,
      currentMin: minVal, // Start with full range
      currentMax: maxVal,
    };
  }
};

// Watch for product changes to update ranges
watch(
  () => products.value,
  () => calculateRanges(),
  { immediate: true }
);

const activeProducts = computed<ProductType[]>(() => {
  // Add defensive check for products value
  if (!products.value || !Array.isArray(products.value)) {
    console.log(
      "Products is not an array in activeProducts computed:",
      products.value
    );
    return [];
  }

  console.log(
    "ACTIVE_PRODUCTS - Input products array (first few):",
    products.value.slice(0, 3).map((p) => ({
      name: p.name,
      price: getProductPrice(p),
      data: getProductDataGB(p),
    }))
  );

  // First, filter out any products with AF_ categories
  let filteredProducts = products.value.filter((product) => {
    // Skip products without categories
    if (!product.category || !Array.isArray(product.category)) {
      return true; // Keep products without categories
    }

    // Check if ANY category of this product starts with AF_
    return !product.category.some((category) => {
      return shouldExcludeCategory(category);
    });
  });

  // If we have a selected tab, filter by it
  if (
    tabActive.value &&
    tabActive.value !== "all" &&
    tabActive.value !== "CatAll"
  ) {
    filteredProducts = filteredProducts.filter((product) => {
      // Handle case where category might be null or not an array
      if (!product.category || !Array.isArray(product.category)) {
        console.log("Product with missing or invalid category:", product);
        return false;
      }

      return product.category.some((category) => {
        return category.code === tabActive.value && category.parentId;
      });
    });
  }

  if (showFilters.value) {
    // Apply price filter - must be between min and max
    filteredProducts = filteredProducts.filter((product) => {
      const price = getProductPrice(product);
      return (
        price >= priceRange.value.currentMin &&
        price <= priceRange.value.currentMax
      );
    });

    // Apply data filter - must be between min and max
    filteredProducts = filteredProducts.filter((product) => {
      const dataGB = getProductDataGB(product);
      return (
        dataGB >= dataRange.value.currentMin &&
        dataGB <= dataRange.value.currentMax
      );
    });
  }

  // CRITICAL FIX: Force sorting by price first, then by data for same price.
  // Use Array.from to create a new array to ensure sorting takes effect
  const sorted = Array.from(filteredProducts).sort((a, b) => {
    const aPrice = getProductPrice(a) || 0;
    const bPrice = getProductPrice(b) || 0;

    // If prices are equal (within a small threshold for float comparison)
    if (Math.abs(aPrice - bPrice) < 0.01) {
      // Secondary sort by data amount (highest first)
      const aData = getProductDataGB(a) || 0;
      const bData = getProductDataGB(b) || 0;
      return bData - aData; // Reverse order for data (highest first)
    }

    // Primary sort by price (lowest first)
    return aPrice - bPrice;
  });

  console.log(
    "ACTIVE_PRODUCTS - Final sorted products (first few):",
    sorted.slice(0, 3).map((p) => ({
      name: p.name,
      price: getProductPrice(p),
      data: getProductDataGB(p),
    }))
  );

  return sorted;
});

const checkMobile = () => {
  isMobile.value =
    window.innerWidth <= 767 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
};

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});

// Set best value - MODIFIED to disable best value tagging
// This ensures all products are treated equally and only sorted by price
const isBestValue = computed(() => {
  return (productItem) => {
    // Always return false to ensure no products are visually tagged as "best value"
    // This matches our sorting logic which sorts strictly by price
    return false;
  };
});

// Helper function for generating product URLs - now using product IDs instead of slugs
const getPlanUrl = (productItem) => {
  // Debug logging to track product properties
  console.log("PlanLink generating URL for:", {
    productId: productItem?.id,
    productName: productItem?.name,
    productType: productItem?.type,
    planType: productItem?.planType || "data",
  });

  // Ensure we always have a valid product ID
  if (!productItem || !productItem.id) {
    console.log("Warning: Product missing ID, returning # as fallback");
    return "#";
  }

  let generatedUrl = "#";

  // Generate URLs using product ID as it's guaranteed to exist
  if (productItem.type === "mifi") {
    generatedUrl = `/product/mifi-${productItem.id}`;
  } else {
    const planType = productItem.planType || "data";

    if (planType === "voice") {
      generatedUrl = `/product/mobile-${productItem.id}`;
    } else {
      generatedUrl = `/product/data-${productItem.id}`;
    }
  }

  console.log(`Generated URL: ${generatedUrl}`);
  return generatedUrl;
};

// Define the planLink function as a standard function now (not computed)
const planLink = (productItem) => {
  return getPlanUrl(productItem);
};
</script>

<template>
  <!-- Include the OnePassFeature component above the plans section only if banners are available -->
  <!-- The banner was removed at Hisham’s request. -->
  <!-- <OnePassFeature :showBanner="content.showFeatureBanner !== false" /> -->

  <section
    :id="section?.customId || 'onepass-plans-section'"
    class="plan-section-container"
    :class="{ 'mobile-view': isMobile }"
  >
    <div class="container mx-auto px-4 py-0 md:py-2 relative">
      <div v-if="loading" class="py-20 text-center">
        <div class="onepass-logo" v-if="!isMobile">
          <img
            src="/assets/images/onepass/logos/onepass-mobile-icon-logo.svg"
            alt="OnePass Mobile"
            width="80"
          />
        </div>
        <div
          class="spinner mx-auto"
          :class="{ 'mobile-optimized': isMobile }"
        ></div>
        <p class="mt-4 text-gray-600">Loading OnePass plans...</p>
        <div class="loading-progress-container mt-4">
          <div
            class="loading-progress-bar"
            :style="{ width: typeof '75%' === 'string' ? '75%' : '0%' }"
          ></div>
        </div>
      </div>

      <!-- Category tabs for filtering plans (moved inside the main content area) -->
      <div v-if="!loading" class="onepass-wrapper">
        <!-- Category tabs and filter toggle -->
        <div class="category-tabs-container">
          <!-- Filter toggle button (now on left) -->
          <div class="filter-toggle">
            <button
              class="filter-toggle-btn"
              :class="{ active: showFilters }"
              @click="
                showFilters = !showFilters;
                currentSlide = 0;
              "
            >
              <svg
                class="filter-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4h16v2.5l-6 6v7l-4-2v-5l-6-6V4z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span v-if="!showFilters">Filters</span>
              <span v-else>Hide Filters</span>
            </button>
          </div>

          <!-- Vertical divider -->
          <div class="vertical-divider"></div>

          <!-- Category tabs -->
          <div class="category-tabs">
            <button
              v-for="category in uniqueCategories"
              :key="category.code"
              class="category-tab"
              :class="{ active: tabActive === category.code }"
              @click="changeTab(category.code)"
            >
              {{ category.name }}
            </button>
          </div>
        </div>

        <!-- Divider -->
        <div class="filter-divider"></div>

        <!-- Filter sliders (conditionally shown) -->
        <div v-if="showFilters" class="filter-sliders-container">
          <!-- Price filter -->
          <div class="filter-slider">
            <div class="filter-label">
              <span>Price Range</span>
            </div>
            <div class="dual-slider-container">
              <!-- Min price tooltip -->
              <div
                class="slider-tooltip min-tooltip"
                :style="{
                  left:
                    getPositionPercent(
                      priceRange.currentMin,
                      priceRange.min,
                      priceRange.max
                    ) + '%',
                }"
              >
                ${{ priceRange.currentMin }}
              </div>

              <!-- Min price slider -->
              <input
                type="range"
                v-model.number="priceRange.currentMin"
                :min="priceRange.min"
                :max="priceRange.currentMax"
                class="slider min-slider"
              />

              <!-- Max price tooltip -->
              <div
                class="slider-tooltip max-tooltip"
                :style="{
                  left:
                    getPositionPercent(
                      priceRange.currentMax,
                      priceRange.min,
                      priceRange.max
                    ) + '%',
                }"
              >
                ${{ priceRange.currentMax }}
              </div>

              <!-- Max price slider -->
              <input
                type="range"
                v-model.number="priceRange.currentMax"
                :min="priceRange.currentMin"
                :max="priceRange.max"
                class="slider max-slider"
              />
            </div>
          </div>

          <!-- Data filter -->
          <div class="filter-slider">
            <div class="filter-label">
              <span>Data Range</span>
            </div>
            <div class="dual-slider-container">
              <!-- Min data tooltip -->
              <div
                class="slider-tooltip min-tooltip"
                :style="{
                  left:
                    getPositionPercent(
                      dataRange.currentMin,
                      dataRange.min,
                      dataRange.max
                    ) + '%',
                }"
              >
                {{ dataRange.currentMin }}GB
              </div>

              <!-- Min data slider -->
              <input
                type="range"
                v-model.number="dataRange.currentMin"
                :min="dataRange.min"
                :max="dataRange.currentMax"
                class="slider min-slider"
              />

              <!-- Max data tooltip -->
              <div
                class="slider-tooltip max-tooltip"
                :style="{
                  left:
                    getPositionPercent(
                      dataRange.currentMax,
                      dataRange.min,
                      dataRange.max
                    ) + '%',
                }"
              >
                {{ dataRange.currentMax }}GB
              </div>

              <!-- Max data slider -->
              <input
                type="range"
                v-model.number="dataRange.currentMax"
                :min="dataRange.currentMin"
                :max="dataRange.max"
                class="slider max-slider"
              />
            </div>
          </div>
        </div>

        <!-- Plan cards slider display -->
        <div class="onepass-plans-slider">
          <button
            class="slider-nav prev"
            @click="slidePrev"
            :disabled="currentSlide === 0"
          >
            <span>&lt;</span>
          </button>

          <div class="onepass-plans-container" ref="sliderContainer">
            <div
              class="plans-slider-track"
              :style="{
                transform: `translateX(-${currentSlide * (isMobile ? 100 : 25)}%)`,
              }"
            >
              <PlanCard
                v-for="product in activeProducts"
                :key="product.id"
                :product="product"
                :cta-button="true"
                :best-value="isBestValue(product)"
                :link="getPlanUrl(product)"
              />
            </div>
          </div>

          <button
            class="slider-nav next"
            @click="slideNext"
            :disabled="
              currentSlide >= activeProducts.length - (isMobile ? 1 : 4)
            "
          >
            <span>&gt;</span>
          </button>

          <!-- Slider pagination dots -->
          <div class="slider-pagination">
            <button
              v-for="index in Math.ceil(
                activeProducts.length / (isMobile ? 1 : 4)
              )"
              :key="index - 1"
              class="pagination-dot"
              :class="{
                active:
                  Math.floor(currentSlide / (isMobile ? 1 : 4)) === index - 1,
              }"
              :aria-label="`Go to slide ${index}`"
              @click="currentSlide = (index - 1) * (isMobile ? 1 : 4)"
            ></button>
          </div>
        </div>
      </div>

      <!-- Footnotes -->
      <!-- <div class="onepass-footnotes">
        <p>
          ¹ OnePass benefits for eligible orders. <a href="/terms">See terms</a>
        </p>
        <p>² Compared to paying monthly</p>
      </div> -->
    </div>
  </section>
</template>

<style scoped lang="scss">
.plan-section-container {
  width: 100%;
  max-width: 100%;
  padding: 0 0 40px;
  background-color: #fff;
  position: relative;
  font-family: Arial, Helvetica, sans-serif;
  display: flex;
  justify-content: center;
}

.onepass-section-title {
  text-align: center;
  margin-bottom: 20px;
  width: 100%;
}

/* OnePass Mobile tagline styling */
.onepass-tagline-wrapper {
  width: 80%;
  max-width: 80%;
  margin: 0 auto;
  display: block;
}

.onepass-tagline-image {
  width: 100%;
  height: auto;
  display: block;
}

/* Mobile view (100% width) */
@media (max-width: 767px) {
  .onepass-tagline-wrapper {
    width: 100%;
    max-width: 100%;
  }
}

/* Promotional banners styling */
.promotional-banner {
  width: 80%;
  margin: 0 auto 2rem;
  overflow: hidden;
}

.desktop-banner,
.mobile-banner {
  width: 100%;
  height: auto;
  max-width: 100%;
  display: block;
  margin: 0 auto;
  border-radius: 8px;
}

/* Mobile view (100% width) */
@media (max-width: 767px) {
  .promotional-banner {
    width: 100%;
    max-width: 100%;
  }
}

.onepass-plans-slider {
  position: relative;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;

  .slider-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    background-color: #9900f1; /* OnePass brand purple */
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    z-index: 10;
    cursor: pointer;
    color: #fff;
    box-shadow: 0 2px 5px rgba(153, 0, 241, 0.3);

    &.prev {
      left: -40px;
    }

    &.next {
      right: -40px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .onepass-plans-container {
    width: 100%;
    overflow: hidden;
    margin: 0 auto;

    .plans-slider-track {
      display: flex;
      transition: transform 0.3s ease;
      width: 100%;

      & > div {
        flex: 0 0 calc(25% - 20px);
        width: calc(25% - 20px);
        margin: 0 10px;
        box-sizing: border-box;

        /* Mobile responsive adjustments */
        @media (max-width: 767px) {
          flex: 0 0 calc(100% - 20px);
          width: calc(100% - 20px);
        }
      }
    }
  }

  .slider-pagination {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    gap: 8px;

    .pagination-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #e0e0e0;
      border: none;
      padding: 0;
      cursor: pointer;

      &.active {
        background-color: #9900f1; /* OnePass brand color */
      }
    }
  }

  @media (max-width: 768px) {
    /* Maintain consistent padding with header */
    .slider-nav {
      width: 36px;
      height: 36px;
      font-size: 16px;
      &.prev {
        left: -15px;
      }
      &.next {
        right: -15px;
      }
    }

    .pagination-dot {
      width: 8px;
      height: 8px;
    }
  }
}

/* Loading spinner styles */
.onepass-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 40px auto;
  min-height: 200px;
}

.spinner {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3px solid #9900f1; /* OnePass purple */
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  &.mobile-optimized {
    width: 40px;
    height: 40px;
    border-width: 2px;
  }
}

.onepass-logo {
  margin-bottom: 20px;
  font-weight: 500;
  text-align: center;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-progress-container {
  width: 200px;
  height: 4px;
  background-color: #f3f3f3;
  border-radius: 4px;
  margin: 0 auto;
  overflow: hidden;
}

.loading-progress-bar {
  height: 100%;
  background-color: #9900f1; /* OnePass purple */
  border-radius: 4px;
  transition: width 0.5s ease;
}

/* OnePass wrapper styling */
.onepass-wrapper {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Category tabs styling */
.category-tabs-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 auto 15px;
  justify-content: center;
  max-width: 1200px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
}

.category-tabs {
  display: flex;
  gap: 10px;
  max-width: 80%;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 768px) {
    padding-bottom: 5px;
    flex: 1;
    overflow-x: auto;
    justify-content: flex-start;
  }
}

/* Vertical divider between categories and filter */
.vertical-divider {
  height: 30px;
  width: 1px;
  background-color: #e0e0e0;
  margin: 0 5px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    display: none; /* Hide on very small screens */
  }
}

.category-tab {
  padding: 8px 15px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  background-color: white;
  border: 1px solid #ddd;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9900f1; /* OnePass purple */
    color: #9900f1;
  }

  &.active {
    background-color: #f4e7ff; /* Light OnePass purple */
    color: #9900f1;
    border-color: #9900f1;
  }
}

.onepass-plans-heading {
  margin-bottom: 30px;

  h2 {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 10px;
  }

  p {
    font-size: 18px;
    color: #666;
  }
}

/* Filter divider */
.filter-divider {
  width: 100%;
  height: 1px;
  background-color: #e0e0e0;
  margin: 10px 0 15px 0;
}

/* Filter toggle button */
.filter-toggle {
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  .filter-toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: white;
    border: 2px solid #9900f1; /* OnePass purple */
    color: #9900f1;
    font-weight: 600;
    padding: 8px 15px;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &.active,
    &.selected {
      background-color: #f4e7ff; /* Light OnePass purple */
    }

    @media (max-width: 480px) {
      padding: 8px 10px;

      /* On small screens, only show the icon */
      span {
        display: none;
      }
    }

    .filter-icon {
      width: 16px;
      height: 16px;
      stroke: currentColor;
    }

    &:hover {
      background-color: rgba(153, 0, 241, 0.1);
    }

    &:active {
      transform: scale(0.98);
    }
  }
}

/* Filter sliders container */
.filter-sliders-container {
  width: 80%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 auto 25px;
  padding: 0 10px;
}

/* Individual filter slider */
.filter-slider {
  width: 100%;

  .filter-label {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;

    .filter-value {
      color: #9900f1; /* OnePass purple */
      font-weight: 700;
    }
  }

  /* Dual slider container */
  .dual-slider-container {
    position: relative;
    width: 100%;
    height: 30px;
    margin-bottom: 8px;
  }

  /* Range slider styling */
  .slider {
    position: absolute;
    width: 100%;
    height: 6px;
    -webkit-appearance: none;
    appearance: none;
    background: none;
    outline: none;
    pointer-events: none;

    /* Shared track styles */
    &::-webkit-slider-runnable-track {
      width: 100%;
      height: 6px;
      background: #e0e0e0;
      border-radius: 3px;
    }

    &::-moz-range-track {
      width: 100%;
      height: 6px;
      background: #e0e0e0;
      border-radius: 3px;
    }

    /* Shared thumb styles */
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #9900f1; /* OnePass purple */
      cursor: pointer;
      border: 2px solid #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      pointer-events: auto;
      margin-top: -7px; /* Center vertically */
    }

    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #9900f1; /* OnePass purple */
      cursor: pointer;
      border: 2px solid #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      pointer-events: auto;
    }
  }

  /* Min slider specific styles */
  .min-slider {
    z-index: 3; /* Higher z-index to ensure left thumb is on top */
  }

  /* Max slider specific styles */
  .max-slider {
    z-index: 2;
  }

  /* Tooltip to show current values */
  .slider-tooltip {
    position: absolute;
    top: -25px; /* Position above the slider */
    transform: translateX(-50%); /* Center the tooltip */
    background-color: #9900f1; /* OnePass purple */
    color: white;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    pointer-events: none; /* Allow clicking through the tooltip */
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    z-index: 10;
    transition: left 0.2s ease;
  }

  .min-tooltip {
    z-index: 11; /* Ensure min tooltip is above max tooltip if they overlap */
  }
}

/* Mobile responsiveness for filters */
@media (max-width: 768px) {
  .filter-sliders-container {
    padding: 0 5px;
  }
}

.onepass-footnotes {
  max-width: 800px;
  margin: 20px auto 0;
  text-align: center;
  font-size: 12px;
  color: #555;

  a {
    color: #9900f1;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  p {
    margin: 4px 0;
  }
}
</style>
